import { useMemo } from 'react';
import { Building2 } from 'lucide-react';
import { Button } from 'components/ui/button';
import { StoreCard } from './StoreCard';
import { STORE } from 'state/store/type';

export interface OrgViewAccordionProps {
  storesList: STORE.IStore[];
  accessibleStoresCount: number;
  onStoreClick: (store: STORE.IStore) => void;
  onOrgViewClick: () => void;
}

export const OrgViewAccordion = ({
  storesList,
  accessibleStoresCount,
  onStoreClick,
  onOrgViewClick,
}: OrgViewAccordionProps) => {
  const sortedStores = useMemo(
    () =>
      [...storesList].sort(
        (a, b) => Number(b.has_access) - Number(a.has_access)
      ),
    [storesList]
  );

  const storeText = accessibleStoresCount === 1 ? 'store' : 'stores';

  return (
    <div className="w-full mb-4 border border-gray-blue-20 rounded-xl overflow-hidden">
      <div
        className="flex items-center justify-between px-5 py-4 bg-white cursor-pointer transition-all duration-200"
        data-testid="accordion-header"
      >
        <div className="flex items-center flex-1">
          <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-gray-blue-10 mr-2">
            <Building2
              size={20}
              className="text-gray-blue-60"
              aria-hidden="true"
            />
          </div>

          <div className="flex flex-col">
            <span className="text-base font-semibold leading-6 text-gray-blue-90">
              Org view
            </span>
            <span className="text-xs lg:text-sm font-normal leading-[18px] text-gray-blue-60">
              {accessibleStoresCount} {storeText}
            </span>
          </div>
        </div>

        <Button
          variant="outline"
          className="text-sm font-medium leading-5 px-4 py-2 rounded-md max-md:hidden"
          onClick={onOrgViewClick}
          aria-label="Open organization view"
        >
          Open org
        </Button>
      </div>

      <div className="flex flex-col m-4 overflow-y-scroll max-h-[50vh]">
        {sortedStores.map((store, index) => (
          <StoreCard
            key={`${store.organization_id}-${store.dba_name}`}
            store={store}
            hasAccess={store.has_access}
            onClick={onStoreClick}
            isFirst={index === 0}
            isLast={index === sortedStores.length - 1}
          />
        ))}
      </div>
    </div>
  );
};
